import React, { useState } from 'react';
import { Card, Button, Modal, Alert, Spinner } from 'react-bootstrap';
import { LuTriangleAlert, LuTrash2, LuShieldAlert, LuInfo } from "react-icons/lu";
import { useDispatch } from 'react-redux';
import { deleteMasterAccount, logout } from '../../../../redux/reducers/authSlice';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const DeleteAccount = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [typedConfirm, setTypedConfirm] = useState("");

    const handleDelete = async () => {
        if (typedConfirm !== "DELETE") {
            return toast.error("Please type DELETE to confirm");
        }

        setLoading(true);
        try {
            await dispatch(deleteMasterAccount()).unwrap();
            dispatch(logout());
            toast.success("Your account has been permanently removed.");
            navigate('/login');
        } catch (err) {
            toast.error(err || "Failed to delete account");
            setLoading(false);
            setShowConfirm(false);
        }
    };

    return (
        <div className="mt-3">
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <Card.Body className="p-4">
                    <div className="mb-4">
                        <div className="d-flex align-items-center gap-2 text-danger">
                            <LuTriangleAlert size={24} />
                            <h5 className="fw-bold mb-0">Delete your account</h5>
                        </div>
                        <p className="text-muted small mt-2">
                            Once you delete your account, there is no going back. All your businesses,
                            invoice history, customer records, and products will be permanently removed from our servers.
                        </p>
                    </div>

                    <Alert variant="warning" className="border-0 rounded-4 d-flex gap-3 align-items-center">
                        <LuInfo size={20} className="flex-shrink-0" />
                        <div className="small">
                            <strong>Note:</strong> Active subscriptions will be cancelled immediately and no refunds will be issued for remaining time.
                        </div>
                    </Alert>

                    <div className="mt-4 pt-3 border-top d-flex justify-content-between align-items-center">
                        <div>
                            <p className="fw-bold mb-0">Permanently delete this account</p>
                            <small className="text-muted">This action is irreversible.</small>
                        </div>
                        <Button
                            variant="danger"
                            className="rounded-pill px-4 fw-bold shadow-sm d-flex align-items-center gap-2"
                            onClick={() => setShowConfirm(true)}
                        >
                            <LuTrash2 size={18} /> Delete Account
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {/* CONFIRMATION MODAL */}
            <Modal show={showConfirm} onHide={() => !loading && setShowConfirm(false)} centered>
                <Modal.Body className="p-4 text-center">
                    <div className="text-danger mb-3">
                        <LuShieldAlert size={60} />
                    </div>
                    <h4 className="fw-bold">Are you absolutely sure?</h4>
                    <p className="text-muted small px-3">
                        This will permanently delete your master account and all associated invoice data.
                        To confirm, please type <strong>DELETE</strong> in the box below.
                    </p>

                    <div className="mt-4 mb-3 text-start">
                        <input
                            type="text"
                            className="form-control rounded-3 text-center fw-bold border-danger shadow-none"
                            placeholder="Type DELETE"
                            value={typedConfirm}
                            onChange={(e) => setTypedConfirm(e.target.value)}
                            disabled={loading}
                        />
                    </div>

                    <div className="d-flex gap-2 mt-4">
                        <Button
                            variant="light"
                            className="flex-grow-1 rounded-pill fw-bold"
                            onClick={() => setShowConfirm(false)}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            className="flex-grow-1 rounded-pill fw-bold"
                            disabled={typedConfirm !== "DELETE" || loading}
                            onClick={handleDelete}
                        >
                            {loading ? <Spinner size="sm" /> : "Delete Everything"}
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default DeleteAccount;
